import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { createStationTime, deleteStudent, getSchedule, getStationTimes, getStudents, updateStationTime } from "../../../api/shuttleSetting.api";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { IGetSchedule, IGetStationTimes, IGetStudents } from "../../../models/shuttleSetting.model";
import { ShuttleContents } from "../../../pages/campus/shuttle";
import { RootState } from "../../../store/rootReducer";
import { Descriptions, Select, Table, TableColumnsType } from "antd";
import config from '../../../lib/config';
import Pagination from '../../Pagination';
import moment from "moment";
import Button from '../../../components/Button';
import Loader from "../../Loader";
import ErrorResponse from "../../ErrorResponse";
import SelectStudentModal from "./modals/SelectStudentModal";
import ConfirmModal from "../../modals/ConfirmModal";
import { useHistory, useLocation } from 'react-router-dom';
import CustomTimePicker from "../../CustomTimePicker";
import QueryString from "qs";
import useAuth from "../../../hooks/useAuth";

const ScheduleDetail: FC = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const { successNotification } = useNotification();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BU0002 = 탑승 구분
  const BU0002 = commonCodes.find(item => item.commonCode === 'BU0002')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetSchedule>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetStationTimes>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetStudents>();
  const { arr: submitRes, isLoading: submitIsLoading, error: submitError, executeArr: submitExecute } = useAxios();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [stations, setStations] = useState<any[]>([]);
  const [runningStationCode, setRunningStationCode] = useState<number | undefined>();
  const [rideGbn, setRideGbn] = useState('');
  const [students, setStudents] = useState<any[]>([]);
  const [studentPage, setStudentPage] = useState(1);
  const [selectStudentVisible, setSelectStudentVisible] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [rideStudentCode, setRideStudentCode] = useState<number | undefined>();
  const [weekdayDiv, setWeekdayDiv] = useState('');
  const [scheduleCode, setScheduleCode] = useState<number | undefined>();

  useEffect(() => {
    if (BU0002) {
      setRideGbn(BU0002[0].detailCode);
    }
  }, [BU0002]);

  useEffect(() => {
    if (query.weekdayDiv) {
      setWeekdayDiv(String(query.weekdayDiv));
    }
    if (currentCampus && query.scheduleCode) {
      setScheduleCode(Number(query.scheduleCode));
      doGetSchedule(Number(query.scheduleCode));
    }
  }, [currentCampus, location]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      setStations(res2.data);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (res3 && !isLoading3) {
      filterStudents(rideGbn);
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    if (submitRes.length && !submitIsLoading) {
      successNotification(t('common.message.save1'));
      doGetSchedule();
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      if (runningStationCode) onGetStudents(runningStationCode);
      setConfirmDeleteVisible(false);
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const filterStudents = (_rideGbn: string) => {
    if (!res3 || !runningStationCode) return;
    const _students = res3.data.filter(item => item.rideGbn === _rideGbn);
    setStudents(_students);
  }

  // scheduleCode = runningCode
  const doGetSchedule = (_scheduleCode: number|undefined = scheduleCode) => {
    if (!_scheduleCode) return;
    execute(getSchedule(currentCampus, _scheduleCode));
    execute2(getStationTimes(currentCampus, _scheduleCode));
  }

  const onSetStationTime = (index: number, time: string|undefined) => {
    console.log('time', time)
    const _stations = [...stations];
    _stations[index].runningTime = time;
    setStations(_stations);
  }

  const onSubmitStationTime = () => {
    if (!stations.length || !res?.data?.runningCode) return;

    const createReq: any = [];
    const updateReq: any = [];
    stations.forEach(item => {
      if (item.runningStationCode) {
        updateReq.push(item);
      } else {
        createReq.push(item);
      }
    });

    const arr: any = [];
    if (createReq.length) {
      arr.push(createStationTime(currentCampus, res.data.runningCode, createReq));      
    }
    if (updateReq.length) {
      arr.push(updateStationTime(currentCampus, res.data.runningCode, updateReq));
    }
    if (arr.length) {
      submitExecute(arr);
    }
  }

  const onGetStudents = (runningStationCode: number) => {
    execute3(getStudents(currentCampus, runningStationCode));
  }

  const onDeleteStudent = () => {
    if (!rideStudentCode) return;
    deleteExecute(deleteStudent(currentCampus, rideStudentCode));
  }

  const stationColumns: TableColumnsType<any> = [
    {
      title: t('campus.shuttle.column15'),
      dataIndex: 'stationName',
      key: 'stationName',
      align: 'center',
    },
    {
      title: t('campus.shuttle.column27'),
      dataIndex: 'runningTime',
      key: 'runningTime',
      align: 'center',
      render: (value, record, index) => (
        <div className="time-picker__area w-120p m-auto">
          <CustomTimePicker
            value={value}
            onSelect={value => onSetStationTime(index, value ? moment(value).format('HH:mm') : undefined)}
          />
        </div>
      )
    },
    {
      title: t('common.button.select'),
      dataIndex: 'runningStationCode',
      key: 'runningStationCode',
      align: 'center',
      render: (value, record, index) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClick={() => onGetStudents(value)}
            text={t('campus.shuttle.column27')}
            disabled={!value}
          />
        </div>
      )
    },
  ];

  const studentColumns: TableColumnsType<any> = [
    {
      title: t('campus.shuttle.column28'),
      dataIndex: 'memKorName',
      key: 'memKorName',
      align: 'center',
      sorter: (a, b) => a.memKorName?.localeCompare(b.memKorName)
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
      sorter: (a, b) => a.className?.localeCompare(b.className)
    },
    {
      title: t('common.button.delete'),
      dataIndex: 'rideStudentCode',
      key: 'rideStudentCode',
      align: 'center',
      render: (value) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small gray"
            onClick={() => {
              setRideStudentCode(value);
              setConfirmDeleteVisible(true);
            }}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      )
    },
  ];

  return (
    <>
      {isLoading || isLoading2 || isLoading3 || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={deleteError} />

      <div className='comm-table__area rowType'>
        <Descriptions bordered size="small" column={5}>
          <Descriptions.Item label={t('campus.shuttle.column29')}>
            {res?.data.lineName}
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.shuttle.column27')}>
            {res?.data.lineTime}
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.shuttle.column24')}>
            {res?.data.busNo}
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.shuttle.column25')}>
            {res?.data.attendMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.shuttle.column26')}>
            {res?.data.overMemKorName}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div>
        <div className='comm-bx__space'>
          <div className='comm-btn__area right'>
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('campus.shuttle.placeholder5')}
                value={rideGbn}
                onChange={value => {
                  filterStudents(value);
                  setRideGbn(value);
                }}
              >
                {
                  BU0002?.map((item: any) => {
                    return (
                      <Select.Option
                        key={item.detailCode}
                        value={item.detailCode}
                      >
                        {item.korName}
                      </Select.Option>
                    )
                  })
                }
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6">
          <div>
            <Table
              className="comm-table__area mt-0"
              columns={stationColumns}
              dataSource={stations}
              pagination={{
                pageSize: 9999999
              }}
              rowClassName={(record: any) => record.runningStationCode === runningStationCode ? 'chosen' : 'pointer'}
              onRow={(record: any) => {
                return {
                  onClick: () => {
                    setRunningStationCode(record.runningStationCode);
                  }
                };
              }}
            />
          </div>
          <div className='comm-bx__between'>
            <div className="comm-btn__area">
              <Button
                className="btn-basic gray-line"
                onClick={() => history.push(`/campus/shuttle?index=${ShuttleContents.ScheduleList}&weekdayDiv=${weekdayDiv}`)}
                text={t('common.button.list')}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic pink"
                onClick={onSubmitStationTime}
                text={t('common.button.save')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>
        <div className="col-6">
          <div>
            <Table
              className="comm-table__area mt-0"
              columns={studentColumns}
              dataSource={students}
              pagination={{
                current: studentPage,
                pageSize: config.pageSize,
              }}
              rowClassName="pointer"
            />
          </div>
          <div className='comm-bx__between'>
            <div />
            <div className="common-paging center">
              <Pagination
                count={students.length}
                page={studentPage}
                setPage={setStudentPage}
              />
            </div>
            <div className="comm-btn__area right">
              <Button
                className="btn-basic pink-line"
                onClick={() => setSelectStudentVisible(true)}
                text={t('common.button.add')}
                disabled={!runningStationCode}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>
      </div>

      {selectStudentVisible &&
        <SelectStudentModal
          visible={selectStudentVisible}
          setVisible={setSelectStudentVisible}
          rideGbn={rideGbn}
          runningStationCode={runningStationCode}
          onGetStudents={onGetStudents}
        />      
      }
      {confirmDeleteVisible &&
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDeleteStudent}
          titleText={t('system.menu.title6')}
          messageText={t('campus.shuttle.text10')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      }
    </>
  );
}

export default ScheduleDetail;
